import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { drivesKeys, useCurrentDriveSyncRun, useDriveSyncRuns, useResyncDrive, useSyncDriveNow } from '@/hooks';
import { getAxiosErrorDisplayMessage } from '@/utils/error-utils';
import { formatTimeAgo } from '@/utils/timeUtils';
import type { DriveSafe, DriveSyncPhaseValue, DriveSyncRunSafe } from '@vivport/backend/types';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

type DriveSyncPanelProps = {
  drive: DriveSafe;
};

const PHASE_LABELS: Record<DriveSyncPhaseValue, string> = {
  QUEUED: 'Queued',
  ENUMERATING: 'Reading changes',
  APPLYING: 'Applying',
  DONE: 'Done',
  FAILED: 'Failed',
};

function formatDuration(ms: number): string {
  const totalSeconds = Math.max(0, Math.round(ms / 1000));
  if (totalSeconds < 60) {
    return `${totalSeconds}s`;
  }
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes < 60) {
    return seconds > 0 ? `${minutes} min ${seconds}s` : `${minutes} min`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes > 0 ? `${hours}h ${remainingMinutes} min` : `${hours}h`;
}

function counts(run: DriveSyncRunSafe): string {
  return `+${run.created} ~${run.updated} -${run.deleted}`;
}

function runDurationMs(run: DriveSyncRunSafe, now: number): number {
  const start = new Date(run.startedAt).getTime();
  const end = run.finishedAt ? new Date(run.finishedAt).getTime() : now;
  return end - start;
}

function LiveRun({ run }: { run: DriveSyncRunSafe }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const elapsedMs = runDurationMs(run, now);
  const elapsedSeconds = elapsedMs / 1000;
  const rate = elapsedSeconds > 0 ? run.itemsSeen / elapsedSeconds : 0;

  let remaining: string | null = null;
  if (rate > 0) {
    if (run.totalItemsHint !== null) {
      const left = Math.max(0, run.totalItemsHint - run.itemsSeen);
      remaining = `≈ ${formatDuration((left / rate) * 1000)} remaining`;
    } else {
      remaining = `This source does not report a total; at this rate 1,000 items take ≈ ${formatDuration((1000 / rate) * 1000)}`;
    }
  }

  return (
    <div className="space-y-1 text-sm">
      <div className="flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="font-medium">{PHASE_LABELS[run.phase]}</span>
        <span className="text-muted-foreground">{run.reason === 'MANUAL' ? 'manual' : 'scheduled'}</span>
      </div>
      <p className="text-muted-foreground">
        {run.pagesFetched} page{run.pagesFetched === 1 ? '' : 's'} · {run.itemsSeen.toLocaleString()} items seen ·{' '}
        {counts(run)} · elapsed {formatDuration(elapsedMs)}
        {rate > 0 ? ` · ${Math.round(rate)} items/s` : ''}
      </p>
      {remaining ? <p className="text-muted-foreground">{remaining}</p> : null}
    </div>
  );
}

function HistoryRow({ run }: { run: DriveSyncRunSafe }) {
  const finished = run.phase === 'DONE' || run.phase === 'FAILED';
  return (
    <li className="space-y-1 border-t py-2 text-xs first:border-t-0">
      <div className="flex flex-wrap items-center gap-2">
        <span>{new Date(run.startedAt).toLocaleString()}</span>
        <Badge variant={run.phase === 'FAILED' ? 'destructive' : 'outline'}>{PHASE_LABELS[run.phase]}</Badge>
        <span className="text-muted-foreground">{run.reason === 'MANUAL' ? 'manual' : 'scheduled'}</span>
        {finished ? (
          <span className="text-muted-foreground">{formatDuration(runDurationMs(run, Date.now()))}</span>
        ) : null}
        <span className="text-muted-foreground">
          {counts(run)} · seen {run.itemsSeen.toLocaleString()} in {run.pagesFetched} page{run.pagesFetched === 1 ? '' : 's'}
          {run.fullEnumeration ? ' · full read' : ''}
        </span>
      </div>
      {run.error ? <p className="text-destructive">{run.error}</p> : null}
    </li>
  );
}

export function DriveSyncPanel({ drive }: DriveSyncPanelProps) {
  const queryClient = useQueryClient();
  const currentRun = useCurrentDriveSyncRun(drive.id);
  const syncNow = useSyncDriveNow();
  const resync = useResyncDrive();
  const [historyOpen, setHistoryOpen] = useState(false);
  const runs = useDriveSyncRuns(drive.id, historyOpen);
  const wasInFlight = useRef(false);

  const live = currentRun.data?.run ?? null;
  useEffect(() => {
    if (live) {
      wasInFlight.current = true;
      return;
    }
    if (wasInFlight.current) {
      wasInFlight.current = false;
      void queryClient.invalidateQueries({ queryKey: drivesKeys.all });
    }
  }, [live, queryClient]);

  const handleSyncNow = () => {
    syncNow.mutate(drive.id, {
      onSuccess: () => toast.success(`Sync started for ${drive.name}.`),
      onError: (error: unknown) => toast.error(getAxiosErrorDisplayMessage(error, 'Failed to start the sync.')),
    });
  };

  const handleResync = () => {
    if (
      !window.confirm(
        `Re-read the whole drive ${drive.name}; references not found again will be removed. Continue?`
      )
    ) {
      return;
    }
    resync.mutate(drive.id, {
      onSuccess: () => toast.success(`Full re-read started for ${drive.name}.`),
      onError: (error: unknown) => toast.error(getAxiosErrorDisplayMessage(error, 'Failed to start the re-read.')),
    });
  };

  const lastRun = drive.lastRun;
  const referenced = `${drive.referenceCount.toLocaleString()} file${drive.referenceCount === 1 ? '' : 's'} referenced`;
  const inFlight = live !== null || syncNow.isPending || resync.isPending;
  const Chevron = historyOpen ? ChevronUp : ChevronDown;

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm">
          {lastRun ? (
            <p>
              Last synced {formatTimeAgo(new Date(lastRun.finishedAt ?? lastRun.startedAt))}: {counts(lastRun)} (
              {referenced})
            </p>
          ) : (
            <p className="text-muted-foreground">Never synced ({referenced})</p>
          )}
          {lastRun?.phase === 'FAILED' && lastRun.error ? (
            <p className="text-destructive">Last sync failed: {lastRun.error}</p>
          ) : null}
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="outline" size="sm" onClick={handleSyncNow} disabled={inFlight}>
            {syncNow.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Sync now
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={handleResync} disabled={inFlight}>
            {resync.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Re-read drive
          </Button>
        </div>
      </div>

      {live ? <LiveRun run={live} /> : null}

      <button
        type="button"
        onClick={() => setHistoryOpen((prev) => !prev)}
        aria-expanded={historyOpen}
        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
      >
        <Chevron className="h-3 w-3" />
        Sync history
      </button>
      {historyOpen ? (
        <div className="divide-y">
          {runs.isLoading ? <p className="py-2 text-xs text-muted-foreground">Loading history...</p> : null}
          {runs.isError ? (
            <p className="py-2 text-xs text-destructive">
              {getAxiosErrorDisplayMessage(runs.error, 'Failed to load sync history.')}
            </p>
          ) : null}
          {runs.data && runs.data.runs.length === 0 ? (
            <p className="py-2 text-xs text-muted-foreground">No runs yet.</p>
          ) : null}
          {runs.data && runs.data.runs.length > 0 ? (
            <ul>
              {runs.data.runs.map((run) => (
                <HistoryRow key={run.id} run={run} />
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
